import React from 'react';
import { Box, Typography, LinearProgress, Button, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { formatCount, TrialLimitNotice, trialUsage } from '../../modules/pricingJourney/components/TrialBanner';

interface OptimizationLimitWidgetProps {
  onLimitReached: (reached: boolean) => void; // Comunicación con el padre para bloquear el botón
}

const OptimizationLimitWidget: React.FC<OptimizationLimitWidgetProps> = ({ onLimitReached }) => {
  const { user, pricingAccess } = useAuth();
  // PRC-020: en prueba manda el límite de la prueba (1.000), no el mensual.
  const isTrial = pricingAccess?.state === 'trial';
  const trial = trialUsage(pricingAccess);

  // 1. Obtener datos con seguridad
  const usage = isTrial ? trial.used : (user?.usage_optimizations_this_month || 0);
  const limit = isTrial ? trial.limit : (user?.tier_details?.limit_optimizations_per_month || 10);
  
  // Si el límite es gigante (Growth/Autonomy), no mostramos nada o un mensaje sutil
  const isUnlimited = !isTrial && limit > 1000;

  // 2. Calcular estado
  const isLimitReached = usage >= limit;
  const percentage = Math.min((usage / limit) * 100, 100);

  // 3. Notificar al padre (Page) si debe bloquear
  React.useEffect(() => {
    onLimitReached(isLimitReached && !isUnlimited);
  }, [isLimitReached, isUnlimited, onLimitReached]);

  // CÁLCULO DE FECHA: Siempre el día 1 del mes siguiente
  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const resetDate = nextMonth.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });

  if (isUnlimited) {
    // Para usuarios PRO/Enterprise, no mostramos límites, quizás un badge de "Ilimitado"
    return null; 
  }

  if (isTrial) {
    if (isLimitReached) return <Box sx={{ mb: 3 }}><TrialLimitNotice /></Box>;
    return (
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold">Uso de la prueba</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCount(usage)} de {formatCount(limit)} optimizaciones
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={percentage} sx={{ height: 8, borderRadius: 4 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: isLimitReached ? 'error.main' : 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold" color={isLimitReached ? 'error' : 'text.primary'}>
          Uso del Plan Discovery
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {usage} / {limit} consultas
        </Typography>
      </Box>

      <LinearProgress 
        variant="determinate" 
        value={percentage} 
        color={isLimitReached ? "error" : "primary"}
        sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Se reinicia {resetDate}
        </Typography>
        
        {isLimitReached && (
          <Button 
            component={Link} 
            to="/pricing" 
            size="small" 
            variant="outlined" 
            color="error"
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Aumentar Límites
          </Button>
        )}
      </Box>
      
      {isLimitReached && (
        <Alert severity="warning" sx={{ mt: 2, py: 0 }}>
          Has agotado tus optimizaciones gratuitas de este mes.
        </Alert>
      )}
    </Box>
  );
};

export default OptimizationLimitWidget;